import React, { useState } from 'react';
import { useGameStore } from '../../store/gameStore';
import { Card } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Users, Search, FileText, MapPin, ChevronDown, ChevronUp, Skull, Shield, Lightbulb } from 'lucide-react';

export const RightPanel = ({ onPanelExpand }) => {
  const {
    suspects,
    clues,
    documents,
    locations,
    discovered,
    hintedClues,
    newlyDiscovered,
    newlyHinted,
    selectedTheme,
    selectedDifficulty,
    guideCharacter,
    caseData,
    setSelectedCharacter,
    markAsViewed,
    markHintedAsViewed,
    setPreSelectedAction,
    isTutorial
  } = useGameStore();
  // In tutorial mode, default to Locations expanded so user can see crime scene
  const [expandedSection, setExpandedSection] = useState(isTutorial ? 'locations' : 'people');

  // Hide new item indicators for hard mode
  const showNewIndicators = selectedDifficulty !== 'hard';

  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493',
  }[selectedTheme || 'noir'];

  const toggleSection = (section) => {
    const isExpanding = expandedSection !== section;
    setExpandedSection(expandedSection === section ? null : section);
    // Notify parent when a panel is toggled (for tutorial)
    // In tutorial mode, fire on any click (even if already expanded) to handle back/forward navigation
    if (onPanelExpand && (isExpanding || isTutorial)) {
      onPanelExpand(section);
    }
  };

  // Filter items by discovered status (with defensive checks for undefined)
  const discoveredSuspects = (suspects || []).filter(s => discovered?.suspects?.includes(s.id));
  const discoveredClues = (clues || []).filter(c => discovered?.clues?.includes(c.id));
  const discoveredDocuments = (documents || []).filter(d => discovered?.documents?.includes(d.id));
  const discoveredLocations = (locations || []).filter(l => discovered?.locations?.includes(l.id));
  const hintedClueObjects = (clues || []).filter(c => hintedClues?.includes(c.id));

  // Check if categories have new items (hidden in hard mode)
  const hasNewSuspects = showNewIndicators && (newlyDiscovered?.suspects?.length > 0);
  const hasNewLocations = showNewIndicators && (newlyDiscovered?.locations?.length > 0);
  const hasNewClues = showNewIndicators && (newlyDiscovered?.clues?.length > 0);
  const hasNewDocuments = showNewIndicators && (newlyDiscovered?.documents?.length > 0);
  const hasNewHints = showNewIndicators && (newlyHinted?.length > 0);

  const handleCharacterClick = (character, type) => {
    setSelectedCharacter({ ...character, type });
  };

  const handleGuideClick = (guide) => {
    setPreSelectedAction('consult_guide', 'guide');
    setSelectedCharacter({ ...guide, type: 'guide' });
  };

  const handleSuspectClick = (suspect) => {
    setPreSelectedAction('interview', suspect.id);
    setSelectedCharacter({ ...suspect, type: 'suspect' });
  };

  const handleLocationClick = (location) => {
    setPreSelectedAction('travel', location.id);
  };

  const handleClueClick = (clue) => {
    // Clues should examine their location
    if (discoveredLocations.some(location => location.id === clue.location)) {
      setPreSelectedAction('examine', clue.location);
    }
    // `kind` says what was selected; the clue keeps its own `type`
    // (forensic, documentary, ...) for the detail subtitle.
    setSelectedCharacter({ ...clue, kind: 'clue' });
  };

  const handleDocumentClick = (doc) => {
    setPreSelectedAction('read', doc.id);
    setSelectedCharacter({ ...doc, kind: 'document' });
  };

  return (
    <div className="p-3 md:p-4 space-y-3 md:space-y-4">
      {/* People Section - Victim, Guide, Suspects */}
      <Card className="bg-black/40 backdrop-blur-sm border-white/10" data-section="people">
        <button
          className="w-full min-h-touch p-3 md:p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
          onClick={() => toggleSection('people')}
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <Users className="w-5 h-5" style={{ color: themeAccent }} />
              {hasNewSuspects && (
                <span className="absolute -top-1 -right-1 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-500"></span>
                </span>
              )}
            </div>
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2">
                People
                {hasNewSuspects && <span className="text-xs text-yellow-400 font-normal animate-pulse">• New</span>}
              </h3>
              <p className="text-xs text-gray-400">{2 + discoveredSuspects.length} discovered</p>
            </div>
          </div>
          {expandedSection === 'people' ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {expandedSection === 'people' && (
          <ScrollArea className="h-[280px] md:h-[350px] lg:h-[400px] px-3 md:px-4 pb-3 md:pb-4">
            <div className="space-y-2 md:space-y-3 pr-3 md:pr-4">
              {/* Victim */}
              {caseData?.victim && (
                <div 
                  className="p-3 bg-black/30 rounded-lg hover:bg-black/50 transition-colors cursor-pointer border-l-2 border-red-500"
                  onClick={() => handleCharacterClick(caseData.victim, 'victim')}
                >
                  <div className="flex gap-3 items-center">
                    <div className="relative">
                      <img
                        src={caseData.victim.portrait || 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=100&h=100&fit=crop'}
                        alt={caseData.victim.name}
                        className="w-12 h-12 rounded-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=100&h=100&fit=crop';
                        }}
                      />
                      <Skull className="w-4 h-4 text-red-500 absolute -top-1 -right-1" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white text-sm font-medium truncate">{caseData.victim.name}</h4>
                      <p className="text-xs text-gray-400">Victim - {caseData.victim.occupation}</p>
                      <p className="text-xs text-gray-500 mt-1">Age: {caseData.victim.age}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Guide Character */}
              {guideCharacter && (
                <div
                  className="p-3 bg-black/30 rounded-lg hover:bg-black/50 transition-colors cursor-pointer border-l-2 border-blue-500"
                  data-guide-character="true"
                  onClick={() => handleGuideClick(guideCharacter)}
                >
                  <div className="flex gap-3 items-center">
                    <div className="relative">
                      <img
                        src={guideCharacter.portrait}
                        alt={guideCharacter.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <Shield className="w-4 h-4 text-blue-500 absolute -top-1 -right-1" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white text-sm font-medium truncate">{guideCharacter.name}</h4>
                      <p className="text-xs text-gray-400">{guideCharacter.role}</p>
                      <p className="text-xs text-gray-500 mt-1">Your Guide</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Suspects */}
              {discoveredSuspects.map((suspect) => {
                const isNew = showNewIndicators && newlyDiscovered?.suspects?.includes(suspect.id);
                return (
                  <div
                    key={suspect.id}
                    data-suspect-id={suspect.id}
                    className={`p-3 bg-black/30 rounded-lg hover:bg-black/50 transition-colors cursor-pointer relative ${
                      isNew ? 'animate-pulse ring-2 ring-yellow-400 border-2 border-yellow-400' : ''
                    }`}
                    onClick={() => handleSuspectClick(suspect)}
                    onMouseEnter={() => isNew && markAsViewed('suspects', suspect.id)}
                  >
                    {isNew && (
                      <span className="absolute top-1 right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-500"></span>
                      </span>
                    )}
                    <div className="flex gap-3">
                      <img
                        src={suspect.portrait}
                        alt={suspect.name}
                        className="w-12 h-12 rounded-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=100&h=100&fit=crop';
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white text-sm font-medium truncate">{suspect.name}</h4>
                        <p className="text-xs text-gray-400">{suspect.occupation}</p>
                        <p className="text-xs text-gray-500 mt-1">Age: {suspect.age}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </Card>

      {/* Locations Section */}
      <Card className="bg-black/40 backdrop-blur-sm border-white/10" data-section="locations">
        <button
          className="w-full min-h-touch p-3 md:p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
          onClick={() => toggleSection('locations')}
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <MapPin className="w-5 h-5" style={{ color: themeAccent }} />
              {hasNewLocations && (
                <span className="absolute -top-1 -right-1 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                </span>
              )}
            </div>
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2">
                Locations
                {hasNewLocations && <span className="text-xs text-orange-400 font-normal animate-pulse">• New</span>}
              </h3>
              <p className="text-xs text-gray-400">{discoveredLocations.length} discovered</p>
            </div>
          </div>
          {expandedSection === 'locations' ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {expandedSection === 'locations' && (
          <ScrollArea className="h-[220px] md:h-[260px] lg:h-[300px] px-3 md:px-4 pb-3 md:pb-4">
            <div className="space-y-2 pr-3 md:pr-4">
              {discoveredLocations.map((location) => {
                const isNew = showNewIndicators && newlyDiscovered?.locations?.includes(location.id);
                return (
                  <div
                    key={location.id}
                    data-location-id={location.id}
                    className={`p-3 bg-black/30 rounded-lg hover:bg-black/50 transition-colors cursor-pointer relative ${
                      isNew ? 'animate-pulse ring-2 ring-orange-400 border-2 border-orange-400' : ''
                    }`}
                    onClick={() => handleLocationClick(location)}
                    onMouseEnter={() => isNew && markAsViewed('locations', location.id)}
                  >
                    {isNew && (
                      <span className="absolute top-1 right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                      </span>
                    )}
                    <h4 className="text-white text-sm font-medium">{location.name}</h4>
                    <p className="text-xs text-gray-400 mt-1 line-clamp-2">{location.description}</p>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </Card>

      {/* Clues Section */}
      <Card className="bg-black/40 backdrop-blur-sm border-white/10" data-section="clues">
        <button
          className="w-full min-h-touch p-3 md:p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
          onClick={() => toggleSection('clues')}
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-5 h-5" style={{ color: themeAccent }} />
              {hasNewClues && (
                <span className="absolute -top-1 -right-1 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
              )}
            </div>
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2">
                Clues
                {hasNewClues && <span className="text-xs text-green-400 font-normal animate-pulse">• New</span>}
              </h3>
              <p className="text-xs text-gray-400">{discoveredClues.length} discovered</p>
            </div>
          </div>
          {expandedSection === 'clues' ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {expandedSection === 'clues' && (
          <ScrollArea className="h-[220px] md:h-[260px] lg:h-[300px] px-3 md:px-4 pb-3 md:pb-4">
            <div className="space-y-2 pr-3 md:pr-4">
              {discoveredClues.map((clue) => {
                const isNew = showNewIndicators && newlyDiscovered?.clues?.includes(clue.id);
                return (
                  <div
                    key={clue.id}
                    data-clue-id={clue.id}
                    className={`p-2 md:p-3 bg-black/30 rounded-lg hover:bg-black/50 transition-colors cursor-pointer relative ${
                      isNew ? 'animate-pulse ring-2 ring-green-400 border-2 border-green-400' : ''
                    }`}
                    onClick={() => handleClueClick(clue)}
                    onMouseEnter={() => isNew && markAsViewed('clues', clue.id)}
                  >
                    {isNew && (
                      <span className="absolute top-1 right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                      </span>
                    )}
                    <div className="flex gap-2 md:gap-3">
                      {clue.image && (
                        <img
                          src={clue.image}
                          alt={clue.name}
                          className="w-14 h-14 md:w-20 md:h-20 rounded object-cover flex-shrink-0"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white text-sm font-medium">{clue.name}</h4>
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{clue.description}</p>
                        <div className="flex items-center gap-2 mt-1 md:mt-2">
                          <span className="text-xs px-2 py-0.5 rounded font-semibold" style={{ backgroundColor: `${themeAccent}20`, color: themeAccent }}>
                            {clue.type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </Card>

      {/* Documents Section */}
      <Card className="bg-black/40 backdrop-blur-sm border-white/10" data-section="documents">
        <button
          className="w-full min-h-touch p-3 md:p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
          onClick={() => toggleSection('documents')}
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <FileText className="w-5 h-5" style={{ color: themeAccent }} />
              {hasNewDocuments && (
                <span className="absolute -top-1 -right-1 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                </span>
              )}
            </div>
            <div>
              <h3 className="text-white font-semibold flex items-center gap-2">
                Documents
                {hasNewDocuments && <span className="text-xs text-blue-400 font-normal animate-pulse">• New</span>}
              </h3>
              <p className="text-xs text-gray-400">{discoveredDocuments.length} available</p>
            </div>
          </div>
          {expandedSection === 'documents' ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {expandedSection === 'documents' && (
          <ScrollArea className="h-[220px] md:h-[260px] lg:h-[300px] px-3 md:px-4 pb-3 md:pb-4">
            <div className="space-y-2 pr-3 md:pr-4">
              {discoveredDocuments.map((doc) => {
                const isNew = showNewIndicators && newlyDiscovered?.documents?.includes(doc.id);
                return (
                  <div
                    key={doc.id}
                    data-document-id={doc.id}
                    className={`p-2 md:p-3 bg-black/30 rounded-lg hover:bg-black/50 transition-colors cursor-pointer relative ${
                      isNew ? 'animate-pulse ring-2 ring-blue-400 border-2 border-blue-400' : ''
                    }`}
                    onClick={() => handleDocumentClick(doc)}
                    onMouseEnter={() => isNew && markAsViewed('documents', doc.id)}
                  >
                    {isNew && (
                      <span className="absolute top-1 right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                      </span>
                    )}
                    <div className="flex gap-2 md:gap-3">
                      {doc.image && (
                        <img
                          src={doc.image}
                          alt={doc.title}
                          className="w-14 h-14 md:w-20 md:h-20 rounded object-cover flex-shrink-0"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white text-sm font-medium">{doc.title}</h4>
                        <p className="text-xs font-semibold mt-1" style={{ color: themeAccent }}>{doc.type}</p>
                        <p className="text-xs text-gray-400 mt-1 md:mt-2 line-clamp-2">{doc.content}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </Card>

      {/* Hints Section - Only show for easy/medium difficulty */}
      {selectedDifficulty !== 'hard' && hintedClueObjects.length > 0 && (
        <Card className={`bg-black/40 backdrop-blur-sm border-white/10 ${hasNewHints ? 'border-yellow-500/60' : 'border-yellow-500/30'}`}>
          <button
            className="w-full min-h-touch p-3 md:p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
            onClick={() => toggleSection('hints')}
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
                {hasNewHints && (
                  <span className="absolute -top-1 -right-1 flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-500"></span>
                  </span>
                )}
              </div>
              <div>
                <h3 className="text-white font-semibold flex items-center gap-2">
                  Hints
                  {hasNewHints && <span className="text-xs text-yellow-400 font-normal animate-pulse">New</span>}
                  <span className="text-xs text-yellow-400 font-normal">• {hintedClueObjects.length} clue{hintedClueObjects.length !== 1 ? 's' : ''} mentioned</span>
                </h3>
                <p className="text-xs text-gray-400">Examine these locations to find them</p>
              </div>
            </div>
            {expandedSection === 'hints' ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {expandedSection === 'hints' && (
            <ScrollArea className="h-[220px] md:h-[260px] lg:h-[300px] px-3 md:px-4 pb-3 md:pb-4">
              <div className="space-y-2 pr-3 md:pr-4">
                {hintedClueObjects.map((clue) => {
                  const location = (locations || []).find(l => l.id === clue.location);
                  const isNewHint = showNewIndicators && newlyHinted?.includes(clue.id);
                  return (
                    <div
                      key={clue.id}
                      className={`p-2 md:p-3 bg-yellow-900/20 rounded-lg hover:bg-yellow-900/30 transition-colors cursor-pointer border ${
                        isNewHint
                          ? 'border-yellow-400 ring-1 ring-yellow-400/50'
                          : 'border-yellow-500/30'
                      }`}
                      onClick={() => {
                        // Pre-select travel to this location
                        if (location) {
                          setPreSelectedAction('travel', location.id);
                        }
                      }}
                      onMouseEnter={() => isNewHint && markHintedAsViewed(clue.id)}
                    >
                      <div className="flex gap-2 md:gap-3">
                        <Lightbulb className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white text-sm font-medium">{clue.name}</h4>
                          {location && (
                            <p className="text-xs text-yellow-400 mt-1 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              Located at: {location.name}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-1 md:mt-2 italic">
                            Mentioned during investigation - examine {location?.name || 'the location'} to find this clue
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </Card>
      )}
    </div>
  );
};